import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { MuiTelInput } from 'mui-tel-input';
import { useState } from 'react';
import { t } from 'i18next';
import { Tooltip } from '../../Tooltip';
import { useVrm } from '../../../hooks/useVrm';
import { useThemeSelector } from '../../../contexts/ThemeContext';
import { isValidEmail } from '../../../utils/email';

interface IProps {
  handleNext: (details: {
    email: string;
    vrm: string;
    phone: string;
    receiveSms: boolean;
    receiveEmails: boolean;
  }) => void;
  email: string;
  vrm: string;
  phone: string;
  receiveSms: boolean;
  showVrm?: boolean;
}

const PhoneComponent: React.FC<{
  phone: string;
  setPhone: (phone: string) => void;
  receiveSms: boolean;
  touched: boolean;
}> = ({ phone, setPhone, receiveSms, touched }) => {
  const validatePhone = (value: string): string | null => {
    if (receiveSms && !value) {
      return 'Phone number is required when receiving SMS notifications';
    }
    return null;
  };

  const phoneError = validatePhone(phone);
  return (
    <MuiTelInput
      onlyCountries={['NZ', 'CR']}
      disableDropdown={!window.location.href.includes('?test=true')}
      label={`${t('phoneLabel')}*`}
      margin="normal"
      defaultCountry="NZ"
      value={phone}
      inputProps={{
        maxLength: 16,
      }}
      fullWidth
      error={touched && !!phoneError}
      helperText={touched ? phoneError : ''}
      onChange={(newPhone: string) => setPhone(newPhone)}
    />
  );
};

const ReceiveSMSComponent: React.FC<{
  checkedCreditCard: boolean;
  handleReceiveSmsChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  receiveSms: boolean;
}> = ({ checkedCreditCard, handleReceiveSmsChange, receiveSms }) => (
  <Stack direction={'row'} alignItems="center" justifyContent={'space-between'}>
    <FormControlLabel
      control={
        <Checkbox
          onChange={handleReceiveSmsChange}
          checked={receiveSms}
          disabled={checkedCreditCard}
        />
      }
      label={t('receiveTextNotifications')}
    />
    <Tooltip text={t('phoneTooltip')} />
  </Stack>
);

export const EnterEmail = ({
  handleNext,
  email: initialEmail,
  vrm: initialVrm,
  phone: initialPhone,
  receiveSms: initialReceiveSms,
  showVrm = true,
}: IProps) => {
  const { selectedTheme } = useThemeSelector();
  const { vrm, VrmComponent, setVrm } = useVrm(initialVrm);
  const [email, setEmail] = useState(initialEmail);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const [phone, setPhone] = useState(initialPhone);
  const [receiveSms, setReceiveSms] = useState(initialReceiveSms);

  const validateEmail = (value: string) => {
    if (!value) {
      return t('emailRequired');
    }
    if (!isValidEmail(value)) {
      return t('emailInvalid');
    }
    return null;
  };

  const validateVrm = (value: string) => {
    if (!value) {
      return t('vehicleRegistrationRequired');
    }
    return null;
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    if (touched) {
      setError(validateEmail(newEmail));
    }
  };

  const handleBlur = () => {
    setTouched(true);
    setError(validateEmail(email));
  };

  const validatePhone = (value: string): string | null => {
    if (receiveSms && !value) {
      return t('phoneRequired');
    }
    return null;
  };

  const handleSubmit = () => {
    setTouched(true);
    const emailError = validateEmail(email);
    const vrmError = showVrm ? validateVrm(vrm) : null;
    const phoneError = validatePhone(phone);

    if (!emailError && !vrmError && !phoneError) {
      handleNext({
        email,
        vrm: showVrm ? vrm : initialVrm,
        phone,
        receiveSms,
        receiveEmails: true,
      });
    } else {
      setError(emailError || vrmError || phoneError);
    }
  };

  return (
    <div style={{ paddingRight: 16 }}>
      <FormControl component="fieldset" sx={{ width: '100%' }}>
        <Stack
          direction={'column'}
          alignItems="flex-start"
          justifyContent={'space-between'}
          gap={2}
        >
          {showVrm && (
            <VrmComponent
              vrm={vrm}
              suffix={'*'}
              disabled={false}
              setVrm={setVrm}
              error={touched && !vrm}
              helperText={
                touched && !vrm ? t('vehicleRegistrationRequired') || '' : ''
              }
            />
          )}
          <Stack
            direction={'column'}
            alignItems="flex-start"
            sx={{ width: '100%' }}
          >
            <ReceiveSMSComponent
              checkedCreditCard={false}
              handleReceiveSmsChange={() => setReceiveSms(!receiveSms)}
              receiveSms={receiveSms}
            />
            {receiveSms && (
              <PhoneComponent
                phone={phone}
                setPhone={setPhone}
                receiveSms={receiveSms}
                touched={touched}
              />
            )}
          </Stack>
          <Stack
            direction={'column'}
            alignItems="flex-start"
            sx={{ width: '100%' }}
          >
            <TextField
              label={t('email')}
              value={email}
              onChange={handleEmailChange}
              onBlur={handleBlur}
              sx={{
                width: '100%',
                '& .MuiInputBase-input': {
                  color: selectedTheme.secondaryColor,
                },
              }}
              required
              error={!!error}
              helperText={error}
              type="email"
              autoComplete="email"
              inputProps={{
                'aria-label': 'Email address',
                'data-testid': 'email-input',
                onKeyDown: (e: React.KeyboardEvent) => {
                  if (e.key === 'Enter') {
                    handleSubmit();
                  }
                },
              }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('emailReceiptHelpText')}
            </Typography>
          </Stack>
        </Stack>
        <Button
          variant="contained"
          color="primary"
          sx={{ mb: 2 }}
          onClick={handleSubmit}
          disabled={!!error || !email}
        >
          {t('continue')}
        </Button>
      </FormControl>
    </div>
  );
};
